// frontend/src/pages/Lembretes.jsx

import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  Phone,
  User,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Filter,
  RefreshCw,
  MessageCircle,
  AlertTriangle
} from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import StatusBadge from '../components/common/StatusBadge';
import apiService from '../services/apiService';


const Lembretes = () => {
  const [lembretes, setLembretes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataSelecionada, setDataSelecionada] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [expandidos, setExpandidos] = useState({});
  const [filtroResposta, setFiltroResposta] = useState('todos');
  const [filtroAcao, setFiltroAcao] = useState('todos');

  useEffect(() => {
    carregarLembretes();
  }, [dataSelecionada]);

  const carregarLembretes = async () => {
    try {
      setLoading(true);
      const response = await apiService.cobranca.getLembretes(dataSelecionada);
      
      if (response.data.success) {
        setLembretes(response.data.lembretes);
      }
    } catch (error) {
      console.error('Erro ao carregar lembretes:', error);
      alert('Erro ao carregar lembretes');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpandir = (ordemCliente) => {
    setExpandidos(prev => ({
      ...prev,
      [ordemCliente]: !prev[ordemCliente]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const getRespostaLabel = (resposta) => {
    const labels = {
      'pagara_semana': 'Pagará esta semana',
      'pagara_mes': 'Pagará este mês',
      'sem_previsao': 'Sem previsão',
      'nao_conseguido': 'Não conseguido contato'
    };
    return labels[resposta] || resposta || '-';
  };

  const getAcaoLabel = (acao) => {
    const labels = {
      'aguardando': 'Aguardando pgto após cobrança',
      'presencial': 'Enviar cobrança presencial',
      'negativar': 'Negativar cliente'
    };
    return labels[acao] || acao || '-';
  };

  const getRespostaStatus = (resposta) => {
    const statusMap = {
      'pagara_semana': 'success',
      'pagara_mes': 'warning',
      'sem_previsao': 'danger',
      'nao_conseguido': 'secondary'
    };
    return statusMap[resposta] || 'secondary';
  };

  const getAcaoStatus = (acao) => {
    const statusMap = {
      'aguardando': 'info',
      'presencial': 'warning',
      'negativar': 'danger'
    };
    return statusMap[acao] || 'secondary';
  };

  const isReenvioZap = (lembrete) =>
    lembrete.mensagem_lembrete?.includes('Reenviar cobrança WhatsApp');

  const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

  const lembretiesFiltrados = lembretes.filter(lembrete => {
    if (filtroResposta !== 'todos' && lembrete.resposta !== filtroResposta) {
      return false;
    }
    if (filtroAcao !== 'todos' && lembrete.acao !== filtroAcao) {
      return false;
    }
    return true;
  });

  const mudarData = (dias) => {
    const novaData = new Date(dataSelecionada);
    novaData.setDate(novaData.getDate() + dias);
    setDataSelecionada(novaData.toISOString().split('T')[0]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          <span className="text-gray-600">Carregando lembretes...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Calendar className="w-8 h-8 mr-3 text-blue-600" />
            Lembretes de Cobrança
          </h1>
          <p className="text-gray-600 mt-1">
            Agendamentos programados para acompanhamento
          </p>
        </div>
      </div>

      {/* Seletor de Data */}
      <Card>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => mudarData(-1)}
              variant="outline"
            >
              ← Dia Anterior
            </Button>
            
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-gray-400" />
              <input
                type="date"
                value={dataSelecionada}
                onChange={(e) => setDataSelecionada(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <Button
              onClick={() => mudarData(1)}
              variant="outline"
            >
              Próximo Dia →
            </Button>

            <Button
              onClick={() => setDataSelecionada(new Date().toISOString().split('T')[0])}
              variant="outline"
            >
              Hoje
            </Button>
          </div>

          <Button
            onClick={carregarLembretes}
            variant="outline"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </Card>

      {/* Filtros */}
      <Card>
        <div className="flex items-center space-x-4">
          <Filter className="w-5 h-5 text-gray-400" />
          
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filtrar por Resposta
              </label>
              <select
                value={filtroResposta}
                onChange={(e) => setFiltroResposta(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="todos">Todas as respostas</option>
                <option value="pagara_semana">Pagará esta semana</option>
                <option value="pagara_mes">Pagará este mês</option>
                <option value="sem_previsao">Sem previsão</option>
                <option value="nao_conseguido">Não conseguido contato</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filtrar por Ação
              </label>
              <select
                value={filtroAcao}
                onChange={(e) => setFiltroAcao(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="todos">Todas as ações</option>
                <option value="aguardando">Aguardando pagamento</option>
                <option value="presencial">Cobrança presencial</option>
                <option value="negativar">Negativar cliente</option>
              </select>
            </div>
          </div>
        </div>
      </Card>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total de Lembretes</p>
              <p className="text-2xl font-bold text-gray-900">{lembretiesFiltrados.length}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="bg-orange-100 p-3 rounded-lg">
              <MessageCircle className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Reenvio WhatsApp</p>
              <p className="text-2xl font-bold text-orange-600">
                {lembretiesFiltrados.filter(l => isReenvioZap(l) && (l.valor_em_aberto || 0) > 0).length}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Débitos Quitados</p>
              <p className="text-2xl font-bold text-green-600">
                {lembretiesFiltrados.filter(l => isReenvioZap(l) && (l.valor_em_aberto || 0) === 0).length}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="bg-orange-100 p-3 rounded-lg">
              <AlertCircle className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Cobrança Presencial</p>
              <p className="text-2xl font-bold text-gray-900">
                {lembretiesFiltrados.filter(l => l.cobranca_presencial).length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Lista de Lembretes */}
      {lembretiesFiltrados.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhum lembrete para esta data
            </h3>
            <p className="text-gray-600">
              Não há agendamentos de cobrança para {formatDate(dataSelecionada)}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {lembretiesFiltrados.map((lembrete) => (
            <Card key={lembrete.ordem_cliente} className="hover:shadow-lg transition-shadow">
              <div className="space-y-4">
                {/* Header do Card */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <User className="w-5 h-5 text-gray-400" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        {lembrete.nome_cliente}
                      </h3>
                      <span className="text-sm text-gray-500">
                        #{lembrete.codigo_cliente}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {lembrete.telefone && (
                        <div className="flex items-center text-gray-600">
                          <Phone className="w-4 h-4 mr-2 text-gray-400" />
                          {formatPhone(lembrete.telefone)}
                        </div>
                      )}
                      
                      <div className="flex items-center text-gray-600">
                        <Clock className="w-4 h-4 mr-2 text-gray-400" />
                        Agendado para: {formatDate(lembrete.data_agendamento)}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => toggleExpandir(lembrete.ordem_cliente)}
                    className="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {expandidos[lembrete.ordem_cliente] ? (
                      <ChevronUp className="w-5 h-5 text-gray-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-600" />
                    )}
                  </button>
                </div>

                {/* Aviso de Reenvio WhatsApp */}
                {isReenvioZap(lembrete) && (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                    (lembrete.valor_em_aberto || 0) > 0
                      ? 'bg-orange-50 border border-orange-200 text-orange-800'
                      : 'bg-green-50 border border-green-200 text-green-800'
                  }`}>
                    {(lembrete.valor_em_aberto || 0) > 0 ? (
                      <>
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        <span>
                          Reenvio WhatsApp — cliente ainda possui <strong>{formatCurrency(lembrete.valor_em_aberto)}</strong> em aberto
                          {lembrete.dias_atraso > 0 && ` (${lembrete.dias_atraso} dias de atraso)`}
                        </span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                        <span>Débitos quitados — reenvio não necessário</span>
                      </>
                    )}
                  </div>
                )}

                {/* Mensagem do Lembrete */}
                {lembrete.mensagem_lembrete && (
                  <p className="text-sm text-gray-600 italic">"{lembrete.mensagem_lembrete}"</p>
                )}

                {/* Status Badges */}
                <div className="flex flex-wrap gap-2">
                  {lembrete.cobranca_zap && (
                    <StatusBadge status="success">
                      ✓ WhatsApp Enviado
                    </StatusBadge>
                  )}

                  {lembrete.cobranca_presencial && (
                    <StatusBadge status="warning">
                      Cobrança Presencial
                    </StatusBadge>
                  )}

                  {lembrete.resposta && (
                    <StatusBadge status={getRespostaStatus(lembrete.resposta)}>
                      {getRespostaLabel(lembrete.resposta)}
                    </StatusBadge>
                  )}

                  {lembrete.acao && (
                    <StatusBadge status={getAcaoStatus(lembrete.acao)}>
                      {getAcaoLabel(lembrete.acao)}
                    </StatusBadge>
                  )}
                </div>

                {/* Detalhes Expandidos */}
                {expandidos[lembrete.ordem_cliente] && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Informações de Contato</h4>
                        <div className="space-y-1 text-gray-600">
                          {lembrete.endereco && (
                            <p><strong>Endereço:</strong> {lembrete.endereco}, {lembrete.numero}</p>
                          )}
                          {lembrete.bairro && (
                            <p><strong>Bairro:</strong> {lembrete.bairro}</p>
                          )}
                          {lembrete.cidade && (
                            <p><strong>Cidade:</strong> {lembrete.cidade}/{lembrete.estado}</p>
                          )}
                          {lembrete.cep && (
                            <p><strong>CEP:</strong> {lembrete.cep}</p>
                          )}
                          {lembrete.telefone2 && lembrete.telefone2 !== lembrete.telefone && (
                            <p><strong>Tel. 2:</strong> {formatPhone(lembrete.telefone2)}</p>
                          )}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Status da Cobrança</h4>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                            <span className="text-gray-700">WhatsApp:</span>
                            <span className={lembrete.cobranca_zap ? 'text-green-600 font-medium' : 'text-gray-500'}>
                              {lembrete.cobranca_zap ? 'Sim' : 'Não'}
                            </span>
                          </div>
                          
                          <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                            <span className="text-gray-700">Presencial:</span>
                            <span className={lembrete.cobranca_presencial ? 'text-orange-600 font-medium' : 'text-gray-500'}>
                              {lembrete.cobranca_presencial ? 'Sim' : 'Não'}
                            </span>
                          </div>
                          
                          {lembrete.resposta && (
                            <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                              <span className="text-gray-700">Resposta:</span>
                              <span className="font-medium">
                                {getRespostaLabel(lembrete.resposta)}
                              </span>
                            </div>
                          )}
                          
                          {lembrete.acao && (
                            <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                              <span className="text-gray-700">Ação:</span>
                              <span className="font-medium">
                                {getAcaoLabel(lembrete.acao)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Lembretes;